import { apiConfigured, clientItQuoteScopes, clientStoreItQuoteRequest } from '../api.js'
import {
  clearFieldErrors,
  escapeHtml,
  missingFieldsMessage,
  validateRequiredFields,
} from '../ui.js'
import {
  clearClientSession,
  getClientSession,
  hasPermission,
  isClientLoggedIn,
} from '../clientAuth.js'

export function renderQuoteRequest() {
  return `
    <div class="portal-card create-sheet">
      <div class="create-sheet-head">
        <div>
          <p class="portal-eyebrow">Presupuesto IT</p>
          <h1>Solicitar presupuesto</h1>
          <p class="portal-muted" id="quote-req-context">Describe la necesidad. El equipo comercial te responderá.</p>
        </div>
        <a href="/mi-instalacion" data-link class="portal-btn">Volver al portal</a>
      </div>
      <form id="quote-req-form" class="create-form create-form--auth" enctype="multipart/form-data" novalidate>
        <div class="portal-field" id="quote-req-install-field" hidden>
          <label class="portal-label" for="installation_id">Sede / instalación *</label>
          <select class="portal-input" id="installation_id" name="installation_id"></select>
        </div>
        <div class="portal-field">
          <label class="portal-label" for="scope_type">Alcance *</label>
          <select class="portal-input" id="scope_type" name="scope_type" required>
            <option value="">Cargando…</option>
          </select>
        </div>
        <div class="portal-field">
          <label class="portal-label" for="subject">Asunto *</label>
          <input class="portal-input" id="subject" name="subject" required maxlength="200" autocomplete="off"
            placeholder="Ej. Ampliación WiFi planta 2" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="description">Descripción *</label>
          <textarea class="portal-textarea" id="description" name="description" required maxlength="8000" rows="6"
            placeholder="Qué necesitas, plazos, zonas afectadas…"></textarea>
        </div>
        <div class="portal-field">
          <label class="portal-label" for="contact_name">Contacto</label>
          <input class="portal-input" id="contact_name" name="contact_name" maxlength="160" autocomplete="name" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="contact_email">Email de contacto</label>
          <input class="portal-input" id="contact_email" name="contact_email" type="email" maxlength="190" autocomplete="email" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="contact_phone">Teléfono</label>
          <input class="portal-input" id="contact_phone" name="contact_phone" maxlength="40" autocomplete="tel" />
        </div>
        <div class="portal-field">
          <label class="portal-label" for="notes">Notas adicionales</label>
          <textarea class="portal-textarea" id="notes" name="notes" maxlength="2000" rows="3"
            placeholder="Horario preferente, visita previa…"></textarea>
        </div>
        <div class="portal-field">
          <label class="portal-label" for="attachments">Adjuntos (opcional, máx. 5 · 5 MB c/u)</label>
          <input class="portal-input" id="attachments" name="attachments" type="file" multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt" />
        </div>
        <div class="create-sticky">
          <button type="submit" class="portal-btn portal-btn-primary portal-btn-block">Enviar solicitud</button>
        </div>
      </form>
      <p id="quote-req-msg" class="portal-muted portal-form-msg" role="status"></p>
      <p id="quote-req-success" class="portal-form-msg" hidden></p>
    </div>
  `
}

export function bindQuoteRequest(root, { navigate }) {
  if (!isClientLoggedIn()) {
    navigate('/cliente', { replace: true })
    return
  }

  if (!hasPermission('client_portal.quotes.request') && !getClientSession().staff?.is_superadmin) {
    const denied = root.querySelector('#quote-req-msg')
    if (denied) {
      denied.textContent = 'No tienes permiso para solicitar presupuestos.'
      denied.classList.add('is-error')
    }
    return
  }

  const session = getClientSession()
  const form = root.querySelector('#quote-req-form')
  const msg = root.querySelector('#quote-req-msg')
  const success = root.querySelector('#quote-req-success')
  const btn = form?.querySelector('button[type="submit"]')
  const context = root.querySelector('#quote-req-context')
  const scopeEl = form?.querySelector('#scope_type')
  const installField = root.querySelector('#quote-req-install-field')
  const installEl = form?.querySelector('#installation_id')

  const installs = session.installations || []
  let installationId = installs[0]?.id || null
  try {
    const pref = sessionStorage.getItem('portal_preferred_installation_id')
    if (pref) {
      const prefId = Number(pref)
      if (prefId && installs.some((i) => Number(i.id) === prefId)) installationId = prefId
    }
  } catch (_) {}

  if (installs.length > 1 && installField && installEl) {
    installField.hidden = false
    installEl.innerHTML = installs
      .map(
        (i) =>
          `<option value="${i.id}" ${Number(i.id) === Number(installationId) ? 'selected' : ''}>${escapeHtml(i.name || '')}</option>`
      )
      .join('')
    installEl.addEventListener('change', () => {
      installationId = Number(installEl.value) || null
    })
  }

  const inst = installs.find((i) => Number(i.id) === Number(installationId))
  const orgName =
    session.client?.organization?.name ||
    inst?.organization_name ||
    session.organizations?.[0]?.name ||
    ''
  if (context) {
    context.textContent = [
      orgName ? `Org: ${orgName}` : null,
      inst?.name ? `Sede: ${inst.name}` : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'Describe el alcance del presupuesto.'
  }

  if (form?.contact_name) form.contact_name.value = session.client?.name || session.staff?.name || ''
  if (form?.contact_email) form.contact_email.value = session.client?.email || session.staff?.email || ''
  if (form?.contact_phone) form.contact_phone.value = session.client?.phone || ''

  ;(async () => {
    try {
      const data = await clientItQuoteScopes()
      const scopes = data?.scopes || []
      if (scopeEl) {
        scopeEl.innerHTML =
          `<option value="">Selecciona alcance…</option>` +
          scopes
            .map((s) => `<option value="${escapeHtml(s.value)}">${escapeHtml(s.label)}</option>`)
            .join('')
      }
    } catch (e) {
      if (scopeEl) {
        scopeEl.innerHTML = `
          <option value="expansion">Ampliación de infraestructura</option>
          <option value="new_installation">Nueva instalación IT</option>
          <option value="migration">Migración / renovación</option>
          <option value="refresh">Actualización de equipamiento</option>
          <option value="other">Otro alcance</option>`
      }
      if (msg && e.status === 401) {
        clearClientSession()
        navigate('/cliente', { replace: true })
      }
    }
  })()

  form?.querySelectorAll('input, textarea, select').forEach((el) => {
    el.addEventListener('input', () => {
      clearFieldErrors(el.closest('.portal-field') || form)
      msg?.classList.remove('is-error')
    })
  })

  form?.addEventListener('submit', async (ev) => {
    ev.preventDefault()
    msg.classList.remove('is-error')
    success.hidden = true
    success.innerHTML = ''

    if (!apiConfigured()) {
      msg.textContent = 'Servicio no disponible.'
      msg.classList.add('is-error')
      return
    }

    const { ok, missing } = validateRequiredFields(form, [
      { name: 'scope_type', label: 'Alcance' },
      { name: 'subject', label: 'Asunto' },
      { name: 'description', label: 'Descripción' },
    ])
    if (!ok) {
      msg.textContent = missingFieldsMessage(missing)
      msg.classList.add('is-error')
      return
    }

    const fd = new FormData()
    const iid = Number(installEl?.value || installationId || 0)
    if (iid) fd.append('installation_id', String(iid))
    fd.append('scope_type', String(form.scope_type.value || '').trim())
    fd.append('subject', String(form.subject.value || '').trim())
    fd.append('description', String(form.description.value || '').trim())
    const notes = String(form.notes?.value || '').trim()
    if (notes) fd.append('notes', notes)
    const cName = String(form.contact_name?.value || '').trim()
    const cEmail = String(form.contact_email?.value || '').trim()
    const cPhone = String(form.contact_phone?.value || '').trim()
    if (cName) fd.append('contact_name', cName)
    if (cEmail) fd.append('contact_email', cEmail)
    if (cPhone) fd.append('contact_phone', cPhone)

    const fileInput = form.attachments
    const files = fileInput?.files ? Array.from(fileInput.files).slice(0, 5) : []
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        msg.textContent = `El archivo ${f.name} supera 5 MB.`
        msg.classList.add('is-error')
        return
      }
      fd.append('attachments[]', f, f.name)
    }

    btn && (btn.disabled = true)
    msg.textContent = 'Enviando…'
    try {
      const data = await clientStoreItQuoteRequest(fd)
      msg.textContent = ''
      success.hidden = false
      const reqId = data.request?.id ? `#${data.request.id}` : ''
      success.innerHTML = `${escapeHtml(data.message || 'Solicitud registrada.')}
        ${reqId ? ` <strong>${escapeHtml(reqId)}</strong>` : ''}
        — <a href="/mi-instalacion" data-link>Volver al portal</a>`
      form.reset()
      if (form.contact_name) form.contact_name.value = session.client?.name || ''
      if (form.contact_email) form.contact_email.value = session.client?.email || ''
      if (installEl && installationId) installEl.value = String(installationId)
      btn && (btn.disabled = false)
    } catch (e) {
      if (e.status === 401) {
        clearClientSession()
        navigate('/cliente', { replace: true })
        return
      }
      msg.textContent = e.message || 'Error al enviar la solicitud'
      msg.classList.add('is-error')
      btn && (btn.disabled = false)
    }
  })
}
